<script setup lang="ts">
import { keyBy } from 'lodash';
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores/browserStore';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';

import type { AppSingleSelectOption } from '@libs/ui';
import type {
  ActivityTicket,
  AgeRange,
  Money,
  TicketRate,
  TimeSlot,
} from '@libs/trip-planner-interfaces';
import { AccommodationType } from '@libs/trip-planner-interfaces';
import { AppSvgIcon, AppNumberInput, AppSingleSelect } from '@libs/ui';
import {
  assertValue,
  getFormattedDateTime,
  getFormattedTime,
  formatWithCurrency,
} from '@libs/utils';

import { getAgeRangeAmountText } from '../../stores/activityDetailsTicketsStoreLogic';

const browserStore = useBrowserStore();

interface TicketProps {
  ticketRate: TicketRate;
  productCode: string;
  dateOffset: number;
  initialRateTickets: ActivityTicket[];
  activityType: AccommodationType;
}

const props = defineProps<TicketProps>();

interface TicketEmits {
  (eventName: 'update-selected-status', status: boolean): void;
}

const emit = defineEmits<TicketEmits>();

const preferredTimeStore = usePreferredTimeStore();
const { preferredDay } = storeToRefs(preferredTimeStore);
const recipeStore = useRecipeStore();

const selectedTicketsByAgeRange = ref<Record<string, ActivityTicket>>(
  keyBy(props.initialRateTickets, 'ageRangeId')
);

const selectedTimeSlotId = ref(getInitialSelectedTimeSlot());
function getInitialSelectedTimeSlot(): string | undefined {
  return (
    props.initialRateTickets[0]?.timeSlotId ??
    props.ticketRate.timeSlots?.[0]?.timeSlotId ??
    undefined
  );
}

const totalAmountTicketsSelected = computed(() =>
  Object.values(selectedTicketsByAgeRange.value).reduce(
    (total, current) => (total += current.quantity),
    0
  )
);

const numberTicketsLeft = computed(() =>
  props.ticketRate.maximumAmountTickets
    ? props.ticketRate.maximumAmountTickets - totalAmountTicketsSelected.value
    : undefined
);

function incrementAgeRangeRate(ageRangeId: string): void {
  if (!(ageRangeId in selectedTicketsByAgeRange.value)) {
    selectedTicketsByAgeRange.value[ageRangeId] = {
      ageRangeId,
      quantity: 0,
      rateId: props.ticketRate.rateId,
      timeSlotId: selectedTimeSlotId.value,
      code: props.productCode,
      dateOffset: props.dateOffset,
      supplierToken: props.ticketRate.ageRanges.find(
        (ageRange) => ageRange.ageRangeId === ageRangeId
      )?.supplierToken,
    };
  }

  const activityTicket = selectedTicketsByAgeRange.value[ageRangeId];
  assertValue(
    activityTicket,
    'RateDescription.vue > incrementAgeRangeRate > activityTicket'
  );

  if (props.ticketRate.maximumAmountTickets !== undefined) {
    if (activityTicket.quantity === props.ticketRate.maximumAmountTickets) {
      return;
    }
    if (activityTicket.quantity === props.ticketRate.maximumAmountTickets) {
      return;
    }
  }

  activityTicket.quantity++;

  calculateAndEmitStatus();
}

function decrementAgeRangeRate(ageRangeId: string): void {
  if (!(ageRangeId in selectedTicketsByAgeRange.value)) {
    return;
  }

  const activityTicket = selectedTicketsByAgeRange.value[ageRangeId];
  assertValue(
    activityTicket,
    'RateDescription.vue > decrementAgeRangeRate > activityTicket'
  );

  if (activityTicket.quantity > 1) {
    activityTicket.quantity--;
    return;
  }

  if (activityTicket.quantity === 1) {
    delete selectedTicketsByAgeRange.value[ageRangeId];
  }

  calculateAndEmitStatus();
}

function calculateAndEmitStatus(): void {
  const numberOfSelectedTickets = Object.values(
    selectedTicketsByAgeRange.value
  ).length;
  const isTicketRateValid =
    numberOfSelectedTickets === 0
      ? true
      : isQuantityValid() &&
        isRequiredAgeRangesValid() &&
        isEveryAgeRangeValid();

  emit('update-selected-status', isTicketRateValid);
}

function selectTimeSlot(event: AppSingleSelectOption<string>): void {
  selectedTimeSlotId.value = event.id;

  if (selectedTicketsByAgeRange.value) {
    for (let criteria of Object.values(selectedTicketsByAgeRange.value)) {
      criteria.timeSlotId = selectedTimeSlotId.value;
    }
  }
}

function getFormattedTimeSlot(timeSlot: TimeSlot): string {
  return preferredDay.value
    ? getFormattedDateTime(timeSlot.startTime)
    : getFormattedTime(timeSlot.startTime);
}

watch(selectedTicketsByAgeRange.value, () => {
  recipeStore.updateSelectedTickets(
    Object.values(selectedTicketsByAgeRange.value),
    props.ticketRate.rateId,
    props.dateOffset
  );
});

function isQuantityValid(): boolean {
  return (
    totalAmountTicketsSelected.value === 0 ||
    totalAmountTicketsSelected.value >=
      (props.ticketRate.minimumAmountTickets ?? 0)
  );
}

function isRequiredAgeRangesValid(): boolean {
  return (
    !props.ticketRate.requiredAgeRanges ||
    props.ticketRate.requiredAgeRanges.some((id: string) => {
      const ticketQuantity = selectedTicketsByAgeRange.value[id]?.quantity;
      return ticketQuantity !== undefined ? ticketQuantity > 0 : false;
    }) ||
    Object.keys(selectedTicketsByAgeRange.value).length === 0
  );
}

function isEveryAgeRangeValid(): boolean {
  return props.ticketRate.ageRanges.every((ageRange: AgeRange) => {
    const quantitySelected =
      selectedTicketsByAgeRange.value[ageRange.ageRangeId]?.quantity ?? 0;
    const minimum = ageRange?.minimumTicketsPerAgeRange ?? 0;

    return quantitySelected >= minimum;
  });
}

function getTimeSlotOption(): AppSingleSelectOption<string>[] {
  if (props.ticketRate.timeSlots) {
    return props.ticketRate.timeSlots.map((timeSlot) => ({
      id: timeSlot.timeSlotId,
      label: getFormattedTimeSlot(timeSlot),
    }));
  }

  return [];
}

function getSelectedTimeSlot(): AppSingleSelectOption<string> | null {
  const selected = getTimeSlotOption().find(
    (option) => option.id === selectedTimeSlotId.value
  );

  return selected !== undefined ? selected : null;
}

function getPriceForAgeRange(ageRange: AgeRange, timeSlotId?: string): Money {
  const timeSlotPrice = ageRange.availableTimeslotsPrices?.find(
    (timeSlotPrice) => timeSlotPrice.availabletimeSlotId === timeSlotId
  );

  return timeSlotPrice ?? ageRange.price;
}

// NOTE: Requested by Marcus
const SHOULD_SHOW_ACTIVITY_ADDITIONAL_INFO = false;
</script>

<template>
  <div class="ticket">
    <div class="ticket-header">
      <div class="header-icon">
        <AppSvgIcon
          :name="
            props.activityType === AccommodationType.Golf ? 'golf' : 'activity'
          "
        />
      </div>
      {{ props.ticketRate.title }}
    </div>
    <div class="rate-content">
      <div v-if="props.ticketRate.timeSlots" class="select-time-container">
        <AppSingleSelect
          type="default"
          :options="getTimeSlotOption()"
          :selected="getSelectedTimeSlot()"
          :use-teleport="false"
          @select="selectTimeSlot"
        />
      </div>
      <div
        v-for="ageRange in props.ticketRate.ageRanges"
        :key="ageRange.ageRangeId"
        class="age-range-wrapper"
      >
        <div class="age-range-description-container">
          <div class="age-range-description">
            <span class="age-range-label">
              {{ ageRange.description }}
            </span>
          </div>

          <div class="amount-tickets-wrapper">
            <span
              v-if="!browserStore.isSmallerThan900px"
              class="age-range-price"
            >
              <!-- TODO: long term future proof solution would be to look into MoneyFormatter component and check if it is using global currency and if it can be switched to use product currency only, since this situation applies to any product in Sembo -->
              {{
                formatWithCurrency(
                  getPriceForAgeRange(ageRange, selectedTimeSlotId).amount,
                  getPriceForAgeRange(ageRange, selectedTimeSlotId).currency
                )
              }}
            </span>
            {{ getAgeRangeAmountText(ageRange, selectedTimeSlotId) }}
          </div>
          <span v-if="browserStore.isSmallerThan900px" class="price-mobile">
            {{
              formatWithCurrency(
                getPriceForAgeRange(ageRange, selectedTimeSlotId).amount,
                getPriceForAgeRange(ageRange, selectedTimeSlotId).currency
              )
            }}
          </span>
        </div>
        <AppNumberInput
          :value="selectedTicketsByAgeRange[ageRange.ageRangeId]?.quantity ?? 0"
          :only-stepwise-input="true"
          :disable-decrease="
            !selectedTicketsByAgeRange[ageRange.ageRangeId]?.quantity
          "
          :disable-increase="
            totalAmountTicketsSelected ===
              props.ticketRate.maximumAmountTickets ||
            (!!ageRange.quantityAvailable &&
              selectedTicketsByAgeRange[ageRange.ageRangeId]?.quantity ===
                ageRange.quantityAvailable)
          "
          @decrease="decrementAgeRangeRate(ageRange.ageRangeId)"
          @increase="incrementAgeRangeRate(ageRange.ageRangeId)"
        />
      </div>
      <div
        v-if="
          !!props.ticketRate.maximumAmountTickets &&
          SHOULD_SHOW_ACTIVITY_ADDITIONAL_INFO
        "
        class="summary"
      >
        <div class="summary-text">
          {{
            $t('planner.tickets_available', [
              props.ticketRate.maximumAmountTickets,
            ])
          }}
          <span class="tickets-remaining">
            {{ $t('planner.tickets_remaining', [numberTicketsLeft]) }}
          </span>
        </div>
        <span class="validation-error">
          <AppSvgIcon
            name="warningCircle"
            color="color-brand-summer"
            class="warning-circle-icon"
          />
          {{ $t('planner.tickets_validation_error') }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ticket {
  border-radius: var(--border-radius-8);
  padding: var(--spacing-32);
  box-sizing: border-box;
  background-color: var(--color-white);
  border: 1px solid var(--color-neutral-40);
  display: flex;
  flex-direction: column;

  .ticket-header {
    margin-bottom: var(--spacing-32);
    @include font-label-xxl-bold;
    display: flex;
    align-items: center;
    .header-icon {
      margin-right: var(--spacing-24);
    }
  }
  .rate-content {
    padding-left: var(--spacing-48);
    box-sizing: border-box;
    .age-range-description-container {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      margin-bottom: var(--spacing-24);
    }
    .age-range-wrapper {
      display: flex;
      justify-content: space-between;
      .age-range-description {
        display: flex;

        .age-range-label {
          @include font-label-xl-bold;
        }

        .age-range-price {
          margin: var(--spacing-4) 0;
          color: var(--color-neutral-70);
        }
      }
    }
    .amount-tickets-wrapper {
      @include font-label-l-light;
      color: var(--color-neutral-70);
      margin-top: var(--spacing-4);
    }
    .line-spacer {
      margin: 0px var(--spacing-8);
    }
    .select-time-container {
      width: 140px;
      padding-bottom: var(--spacing-32);
    }

    .summary {
      display: flex;
      justify-content: space-between;
      gap: var(--spacing-16);

      .tickets-remaining {
        margin-left: var(--spacing-4);
        color: var(--color-neutral-70);
      }

      .summary-text {
        @include font-label-l-light;
      }

      .validation-error {
        margin-left: auto;
        // TODO_REBRAND: Color not known yet
        color: #e1bc2f;
        display: flex;
        align-items: center;
        @include font-label-l-light;

        .warning-circle-icon {
          margin-right: var(--spacing-4);
        }
      }
    }
  }
}

@include for-less-or-equal-than-900-px {
  .ticket {
    padding: var(--spacing-16);
    padding-top: var(--spacing-32);

    .ticket-header {
      margin-bottom: var(--spacing-32);
    }

    .rate-content {
      padding: 0;

      .age-range-description {
        display: flex;
        .age-range-price {
          margin-top: var(--spacing-4);
        }
      }

      .price-mobile {
        margin-top: var(--spacing-4);
        @include font-label-l-light;
      }

      .amount-tickets-wrapper {
        align-items: flex-start;
        margin-top: var(--spacing-8);
      }

      .summary {
        flex-flow: column;
        align-items: center;

        .summary-text {
          margin-top: var(--spacing-8);
        }

        .validation-error {
          margin-left: unset;
        }
      }
    }
  }
}
</style>
